import { FormHelperText } from '@mui/material';

import clsx from 'clsx';
import { FieldAttributes, useFormikContext } from 'formik';
import { debounce } from 'lodash';
import { useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';

interface Props<T> {
  field: FieldAttributes<T>;
}

export const Description = <T extends {}>({ field }: Props<T>) => {
  const { errors, touched, setFieldValue, setFieldTouched } = useFormikContext<T>();
  const debouncedChangeHandler = useMemo(
    () =>
      debounce((field, text) => {
        setFieldValue(field, text);
      }, 500),
    [setFieldValue]
  );

  const debouncedBlurHandler = useCallback(
    (field: string) =>
      debounce(() => {
        setFieldTouched(field, true);
      }, 500),
    [setFieldTouched]
  );

  return (
    <>
      <ReactQuill
        className={clsx('funding-modal__rte', {
          'event-modal__rte--error': touched[field.name] && errors[field.name]
        })}
        value={field.value}
        onChange={(_content, _delta, _source, editor) =>
          debouncedChangeHandler(field.name, editor.getHTML())
        }
        theme="snow"
        modules={{
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic'],
            ['link'],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            ['clean']
          ],
          clipboard: {
            allowed: {
              tags: ['a', 'b', 'strong', 'u', 's', 'i', 'p', 'br', 'ul', 'ol', 'li', 'span'],
              attributes: ['href', 'rel', 'target', 'class']
            },
            keepSelection: true,
            substituteBlockElements: true,
            magicPasteLinks: true,
            matchVisual: false
          }
        }}
        formats={['header', 'bold', 'italic', 'link', 'list', 'bullet', 'indent']}
        onBlur={() => {
          debouncedBlurHandler(field.name);
        }}
        bounds={`.quill`}
      />
      {touched[field.name] && errors[field.name] && (
        <FormHelperText error>{errors[field.name]}</FormHelperText>
      )}
    </>
  );
};
